
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { PaymentDialog } from "./PaymentDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SubscriptionCTAProps {
  username: string;
  creatorId: string;
  subscriptionPrice: number;
  lifetimeSubscriptionPrice: number;
  onSubscribe: (subscriptionType: 'monthly' | 'lifetime') => Promise<string>;
  isSubscribed?: boolean;
  subscriptionType?: 'monthly' | 'lifetime';
  onCancelSubscription?: () => Promise<void>;
  isCancelled?:boolean;
}

export const SubscriptionCTA = ({
  username,
  creatorId,
  subscriptionPrice,
  lifetimeSubscriptionPrice,
  onSubscribe,
  isSubscribed = false,
  subscriptionType,
  isCancelled,
  onCancelSubscription,
}: SubscriptionCTAProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [activeSubscriptionType, setActiveSubscriptionType] = useState<'monthly' | 'lifetime' | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);


  const isSubscriptionAvailable = () => {
    return subscriptionPrice || lifetimeSubscriptionPrice;
  }

  const handleSubscribe = async (type: 'monthly' | 'lifetime') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Please log in to subscribe");
        navigate("/login");
        return;
      }

      setIsLoading(true);
      setActiveSubscriptionType(type);

      const secret = await onSubscribe(type);
      
      if (!secret) {
        throw new Error('Failed to create payment intent');
      }

      setClientSecret(secret);
      setShowPaymentDialog(true);

    } catch (error) {
      console.error('Subscription error:', error);
      toast.error("Failed to process subscription");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!onCancelSubscription) return;

    try {
      setIsCancelling(true);
      await onCancelSubscription();
      setShowCancelDialog(false);
      toast.success("Subscription cancelled successfully");
    } catch (error) {
      console.error('Cancel subscription error:', error);
      toast.error("Failed to cancel subscription");
    } finally {
      setIsCancelling(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentDialog(false);
    setClientSecret(null);
    setActiveSubscriptionType(null);
    toast.success("Successfully subscribed!");
  };

  const handlePaymentCancel = () => {
    setShowPaymentDialog(false);
  };

  if (isSubscribed) {
    return (
      <div className="bg-blue-50 p-6 rounded-lg text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <h3 className="text-xl font-semibold text-green-600">
            Subscribed to {username}
          </h3>
        </div>
        {subscriptionType === 'lifetime' ? (
          <p className="text-gray-600 mb-4">
            You have a lifetime subscription
          </p>
        ) : subscriptionType === 'monthly' ? (
          <>
            <p className="text-gray-600 mb-4">
              You have an active monthly subscription
            </p>
            {onCancelSubscription && !isCancelled ? (
              <Button 
                variant="outline" 
                onClick={() => setShowCancelDialog(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Cancel Subscription
              </Button>
            ) : (
              <p className="text-sm text-gray-500">
                Your subscription has been cancelled and will remain active until the end of your billing period
              </p>
            )}

            <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to cancel your subscription to {username}? You'll continue to have access until the end of your current billing period.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="flex justify-end gap-3">
                  <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleCancelSubscription}
                    className="bg-red-600 hover:bg-red-700"
                    disabled={isCancelling}
                  >
                    {isCancelling ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      'Yes, Cancel'
                    )}
                  </AlertDialogAction>
                </div>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <>
      { isSubscriptionAvailable() &&
        <div className="bg-blue-50 p-6 rounded-lg text-center">
        <h3 className="text-xl font-semibold mb-4">
          Subscribe to {username}
        </h3>
        <div className="space-y-6">
          {
            subscriptionPrice && 
            <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-gray-600 mb-2">Monthly Access</p>
            <p className="text-2xl font-bold text-primary mb-3">${subscriptionPrice}/month</p>
            <Button 
              onClick={() => handleSubscribe('monthly')} 
              size="lg" 
              className="w-full"
              disabled={isLoading}
            >
              {isLoading && activeSubscriptionType === 'monthly' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Subscribe Monthly'
              )}
            </Button>
          </div>
          }

         {
          lifetimeSubscriptionPrice && 
          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-primary">
          <p className="text-gray-600 mb-2">Lifetime Access</p>
          <p className="text-2xl font-bold text-primary mb-3">${lifetimeSubscriptionPrice}</p>
          <Button 
            onClick={() => handleSubscribe('lifetime')} 
            size="lg"
            className="w-full bg-primary hover:bg-primary/90"
            disabled={isLoading}
          >
            {isLoading && activeSubscriptionType === 'lifetime' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Get Lifetime Access'
            )}
          </Button>
          <p className="text-sm text-gray-500 mt-2">One-time payment, forever access</p>
        </div>
         }
        </div>
      </div>
      }

      <PaymentDialog
        isOpen={showPaymentDialog}
        onClose={handlePaymentCancel}
        onSuccess={handlePaymentSuccess}
        amount={activeSubscriptionType === 'monthly' ? subscriptionPrice : lifetimeSubscriptionPrice}
        clientSecret={clientSecret}
        username={username}
        creatorId={creatorId}
        subscriptionType={activeSubscriptionType || 'monthly'}
      />
    </>
  );
};
